import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import * as z from "zod";
import { Order } from "@/api.types";
import { isValidPhoneNumber } from "react-phone-number-input";
import { parcelSchema } from "./parcels";
import { taskQueries } from "./tasks";

// Schema
export const createOrderSchema = z.object({
  type: z.enum(["STANDARD", "EXPRESS", "SCHEDULED", "SAME_DAY"]),
  status: z.enum([
    "PENDING",
    "PICKUP_ASSIGNED",
    "PICKED_UP",
    "AT_DEPOT",
    "SORTED",
    "OUT_FOR_DELIVERY",
    "DELIVERED",
    "FAILED",
    "CANCELLED",
    "FIRST_ATTEMPT_FAILED",
    "SECOND_ATTEMPT_FAILED",
    "RETURNED",
  ]),
  senderName: z.string().nonempty("Sender name is required"),
  senderPhone: z.string().nonempty("Sender phone is required"),
  senderEmail: z
    .union([
      z.string().email("Invalid email address"),
      z.string().length(0),
      z.null(),
    ])
    .optional()
    .nullable(),
  recipientName: z.string().nonempty("Recipient name is required"),
  recipientEmail: z
    .union([
      z.string().email("Invalid email address"),
      z.string().length(0),
      z.null(),
    ])
    .optional()
    .nullable(),
  recipientPhone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
  deliveryAddress: z.object({
    line1: z.string().nonempty("Delivery address is required"),
    line2: z.string().optional(),
    city: z.string().nonempty("City is required"),
    province: z.string().nonempty("Province is required"),
    postalCode: z.string().nonempty("Postal code is required"),
    country: z.string().nonempty("Country is required"),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
  pickupAddress: z.object({
    line1: z.string().nonempty("Pickup address is required"),
    line2: z.string().optional(),
    city: z.string().nonempty("City is required"),
    province: z.string().nonempty("Province is required"),
    postalCode: z.string().nonempty("Postal code is required"),
    country: z.string().nonempty("Country is required"),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
  pickupNotes: z.string().optional(),
  deliveryNotes: z.string().optional(),
  parcels: z.array(parcelSchema),
  placerCustomerId: z.string().optional().nullable(),
});

export type CreateOrderSchema = z.infer<typeof createOrderSchema>;

interface OrdersResponse {
  data: Order[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Get orders with pagination and filtering
export function useOrders(
  page: number,
  limit: number,
  status?: Order["status"],
  search?: string,
  type?: string
) {
  const { token } = useSession();

  return useQuery<OrdersResponse>({
    queryKey: ["orders", page, limit, status, search, type],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (status) {
        params.append("status", status);
      }

      if (search) {
        params.append("search", search);
      }

      if (type) {
        params.append("type", type);
      }

      const { data } = await axios.get<OrdersResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// Get order by ID
export function useOrderById(orderId: string) {
  const { token } = useSession();
  return useQuery<Order>({
    queryKey: ["order", orderId],
    queryFn: async () => {
      const { data } = await axios.get<Order>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// Create order mutation
export function useCreateOrder({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<Order, Error, CreateOrderSchema>({
    mutationFn: async (values) => {
      const { data } = await axios.post<Order>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/create`,
        values,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({
        queryKey: taskQueries.activePickupTasks(),
      });
      await queryClient.invalidateQueries({
        queryKey: taskQueries.activeDeliveryTasks(),
      });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
}

// Update order status mutation
export function useCancelOrder() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<Order, Error, { orderId: string }>({
    mutationFn: async ({ orderId }) => {
      const { data } = await axios.patch<Order>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({
        queryKey: taskQueries.activePickupTasks(),
      });
      await queryClient.invalidateQueries({
        queryKey: taskQueries.activeDeliveryTasks(),
      });
      await queryClient.invalidateQueries({ queryKey: ["all-waypoints"] });
    },
  });
}

// Print shipping labels
export function useOrderLabels() {
  const { token } = useSession();

  const printLabels = useMutation({
    mutationFn: async (orderId: string) => {
      const response = await axios.get<Blob>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/shipping-labels`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      return response.data;
    },
  });

  const printBulkLabels = useMutation({
    mutationFn: async (orderIds: string[]) => {
      const response = await axios.post<Blob>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/bulk-shipping-labels`,
        { orderIds },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      return response.data;
    },
  });

  return { printLabels, printBulkLabels };
}

export const updateOrderSchema = createOrderSchema.partial();
export type UpdateOrderSchema = z.infer<typeof updateOrderSchema>;

// Update order mutation
export function useUpdateOrder() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    Order,
    Error,
    { orderId: string; data: UpdateOrderSchema }
  >({
    mutationFn: async ({ orderId, data }) => {
      const { data: responseData } = await axios.patch<Order>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return responseData;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

// Define type for search results
export interface OrderSearchResult {
  id: string;
  orderNumber: string;
  status: string;
  createdAt: string;
  senderName?: string;
  senderPhone?: string;
  recipientName?: string;
  recipientPhone?: string;
  tripNumber?: string;
  waypointSequence?: number;
  parcels?: {
    id: string;
    trackingNumber: string;
    status: string;
    tasks?: {
      id: string;
      type: string;
      status: string;
      trip?: {
        id: string;
        tripNumber: string;
      };
      waypoint?: {
        id: string;
        sequence: number;
      };
    }[];
  }[];
}

interface OrderSearchResponse {
  data: OrderSearchResult[];
  metadata: {
    count: number;
    query: string;
  };
}

// Search orders
export function useOrderSearch(query: string, limit = 20) {
  const { token } = useSession();

  return useQuery<OrderSearchResponse>({
    queryKey: ["orderSearch", query, limit],
    queryFn: async () => {
      if (!query.trim()) return { data: [], metadata: { count: 0, query: "" } };

      const { data } = await axios.get<OrderSearchResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/search`,
        {
          params: {
            query: query.trim(),
            limit,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Handle empty response
      if (!data?.data) {
        return { data: [], metadata: { count: 0, query: query.trim() } };
      }

      return data;
    },
    enabled: !!token && !!query.trim(),
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

// Direct search function for debugging
export async function searchOrdersDirectly(
  token: string,
  query: string,
  limit = 20
): Promise<OrderSearchResponse> {
  console.log(
    `Searching orders directly with query: ${query}, limit: ${limit}`
  );

  try {
    const response = await axios.get<OrderSearchResponse>(
      `${import.meta.env.VITE_API_URL}/api/v1/orders/search`,
      {
        params: {
          query: query.trim(),
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log("Direct search response:", response);
    return response.data;
  } catch (error) {
    console.error("Error in direct search:", error);
    if (axios.isAxiosError(error) && error.response) {
      console.error("Axios error details:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data as Record<string, unknown>,
      });
    }

    // Return empty results with proper structure
    return {
      data: [],
      metadata: {
        count: 0,
        query: query.trim(),
      },
    };
  }
}
